/**
 * Turns the candidate-analysis fetchAnalysis response into the edit-profile
 * form's reset values.
 *
 * `data` is `tabs_details.resume_confirmed_data` — the resume-extraction
 * draft. `candidateDetails` is the response's separate, sibling
 * `candidate_details` object. `alternate_email` lives on `candidateDetails`,
 * not on `data`: it's an application-level field, not part of the resume
 * draft, so reading it off `data` prefills nothing.
 *
 * `jobRequiredLanguages` is the job's own rows (`languages_required` off
 * `JobService.fetchJobById`, fetched separately in index.jsx) — prefilled
 * first and locked in the editor, same as the candidate-facing apply flow.
 *
 * Kept in its own module (rather than inline in index.jsx) so it's
 * unit-testable without pulling in the page's heavy transitive imports
 * (ResumePreview -> react-pdf, etc).
 */
import { mergeRequiredLanguageRows } from "@/utils/languageRequirements";

export const mapAnalysisToFormValues = ({ data, salaryDetails, candidateDetails, jobRequiredLanguages = [] }) => ({
  personal_information: {
    name: data?.personal_information?.full_name || "",
    email: data?.personal_information?.email || "",
    alternate_email: candidateDetails?.alternate_email || "",
    phone: data?.personal_information?.phone || "",
    location: data?.personal_information?.location || "",
    linkedin: data?.personal_information?.linkedin || "",
    portfolio: data?.personal_information?.website || "",
    summary: data?.summary || "",
  },

  skills: {
    list: data?.skills || [],
    // Job-required rows first and locked, then whatever the resume itself
    // reported — same as the apply flow. Pairs, legacy strings and composites
    // all normalize; the payload builder serializes back to strings.
    // Confirmed data: the candidate's own level on a required language stays.
    languages: mergeRequiredLanguageRows(jobRequiredLanguages, data?.languages, { keepReportedLevels: true }),
  },

  experience: (data?.work_experience || []).map((exp) => ({
    company: exp.company || "",
    jobTitle: exp.title || "",
    startDate: exp.start_date || "",
    endDate: exp.end_date || "",
    location: exp.location || "",
    description: exp.description || "",
    achievements: exp.achievements || [],
  })),

  education: (data?.education || []).map((edu) => ({
    institution: edu.institution || "",
    degree: edu.degree || "",
    fieldOfStudy: edu.field_of_study || "",
    startDate: edu.start_date || "",
    endDate: edu.end_date || "",
    grade: edu.grade || "",
  })),

  compensation: {
    current_salary: salaryDetails?.current_salary != null ? String(salaryDetails.current_salary) : "",
    expected_salary: salaryDetails?.expected_salary != null ? String(salaryDetails.expected_salary) : "",
    notice_period: salaryDetails?.notice_period || "",
    total_experience: salaryDetails?.total_experience != null ? String(salaryDetails.total_experience) : "",
  },

  additionalSections: (data?.additional_sections || [])
    .reduce((acc, section) => {
      const title = section.section_title || "";
      const existing = acc.find((s) => s.section_title === title);
      const fieldPairs = Object.entries(section.fields || {})
        .filter(([, value]) => value != null && String(value).trim() !== "")
        .map(([key, value]) => ({ key, value: String(value ?? "") }));

      if (fieldPairs.length === 0) return acc;

      if (existing) {
        existing.items.push(fieldPairs);
      } else {
        acc.push({
          section_title: title,
          items: [fieldPairs],
        });
      }
      return acc;
    }, [])
    .map((section) => ({
      ...section,
      items: section.items.length > 0 ? section.items : [[{ key: "", value: "" }]],
    })),

  social: {
    instagram_url: data?.instagram_url || "",
    facebook_url: data?.facebook_url || "",
    twitter_url: data?.twitter_url || "",
  },
});
